import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import fetchWithAuth from '../../lib/fetchWithAuth';


const TopBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  const isAdminLoggedIn = localStorage.getItem("isAdminLoggedIn") === "true";
  const userInitial = localStorage.getItem("userName")?.charAt(0).toUpperCase() || "U";
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setIsMobileMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      // Notify backend (optional)
      await fetchWithAuth("/api/auth/logout", { method: "POST" ,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("adminToken")
            }`,
        },
      });
    } catch (error) {
      console.error("Logout API failed", error);
    }

    // Clear local data
    localStorage.removeItem("authToken");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("userName");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("isAdminLoggedIn");

    // Close UI elements
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);

    // Redirect
    navigate("/login");
  };


  return (
    <>
      <header className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex justify-between items-center px-6 py-4 fixed top-0 z-50">
        <div className="text-2xl font-extrabold tracking-wide">QUIZORA</div>

        {/* Desktop Nav */}
        {!isAdminRoute && (
          <nav className="hidden md:flex gap-6 text-sm font-medium">
            <Link to="/" className="hover:text-white/90">Home</Link>
            <Link to="/how-it-works" className="hover:text-white/90">How It Works</Link>
            {isLoggedIn && <Link to="/leaderboard" className="hover:text-white/90">Leaderboard</Link>}
            <Link to="/rewards" className="hover:text-white/90">Rewards</Link>
            <Link to="/contact" className="hover:text-white/90">Contact</Link>
          </nav>
        )}

        {/* Auth Section (Desktop) */}
        {!isAdminRoute && (
          <div className="hidden md:flex gap-4 items-center">
            {!isLoggedIn ? (
              <>
                <Link to="/login" className="text-sm text-white font-medium hover:underline">Login</Link>
                <Link to="/register">
                  <button className="bg-white text-indigo-600 px-4 py-2 rounded font-semibold hover:bg-gray-100 text-sm">
                    Register
                  </button>
                </Link>
              </>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-10 h-10 rounded-full bg-white text-indigo-600 font-bold flex items-center justify-center hover:ring-2 hover:ring-white transition"
                  title="Account"
                >
                  {userInitial}
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white shadow-lg rounded-md overflow-hidden z-50">
                    <Link to="/my-contest" onClick={() => setIsDropdownOpen(false)} className="block px-4 py-2 hover:bg-gray-100 text-sm text-gray-700">My Contest</Link>
                    <Link to="/settings" onClick={() => setIsDropdownOpen(false)} className="block px-4 py-2 hover:bg-gray-100 text-sm text-gray-700">Settings</Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Admin Logout (Desktop) */}
        {isAdminRoute && isAdminLoggedIn ? (
          <button
            onClick={handleLogout}
            className="text-sm font-medium bg-white text-red-600 px-4 py-2 rounded hover:bg-red-100"
          >
            Logout
          </button>
        ) : (
          // Hamburger (Mobile)
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        )}

      </header>

      {/* Mobile Sidebar */}
      {!isAdminRoute && isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden">
          <div className="absolute top-0 left-0 w-64 bg-white h-full shadow-lg z-50 p-6 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-6">
                <span className="text-xl font-bold text-indigo-600">QUIZORA</span>
                <button onClick={() => setIsMobileMenuOpen(false)}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <nav className="flex flex-col gap-4">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
                <Link to="/how-it-works" onClick={() => setIsMobileMenuOpen(false)}>How It Works</Link>
                {isLoggedIn && <Link to="/leaderboard" onClick={() => setIsMobileMenuOpen(false)}>Leaderboard</Link>}
                <Link to="/rewards" onClick={() => setIsMobileMenuOpen(false)}>Rewards</Link>
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</Link>
              </nav>
            </div>

            <div className="mt-6">
              {!isLoggedIn ? (
                <>
                  <Link to="/login">
                    <button onClick={() => setIsMobileMenuOpen(false)} className="w-full mb-3 border border-indigo-600 text-indigo-600 py-2 rounded hover:bg-indigo-50">
                      Login
                    </button>
                  </Link>
                  <Link to="/register">
                    <button onClick={() => setIsMobileMenuOpen(false)} className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700">
                      Register
                    </button>
                  </Link>
                </>
              ) : (
                <div className="space-y-2">
                  <Link to="/my-contest" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-gray-800">My Contest</Link>
                  <Link to="/settings" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-gray-800">Settings</Link>
                  <button
                    onClick={handleLogout}
                    className="w-full border border-red-500 text-red-500 py-2 rounded hover:bg-red-50 text-sm"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;
